/**
 * 性能面板（M5-01 / DESIGN.md §8）。
 *
 * 只在开发模式挂载（`import.meta.env.DEV`，跟 `app/test-api.ts` 同一条
 * 纪律：生产构建里连安装都不发生，不是"装了但隐藏"）。显示的都是**实时
 * 真实读数**，不是预先算好的样本：
 * - 每帧重算的块数（增量）——读 `RenderBridge.compositedTiles()` 的差值，
 *   跟 e2e 探针 `data-composited-tiles` 同一个计数器，不是另起一套。
 * - 最近一次 GPU 帧提交耗时——`RenderBridge.lastGpuSubmitMs()`，没有 GPU
 *   后端或还没成功过一次时显示"—"。
 * - 常驻内存——`performance.measureUserAgentSpecificMemory()`，只有跨源
 *   隔离（COOP/COEP，M0-02）时浏览器才会给这个 API，不可用时如实说
 *   "不可用（未跨源隔离）"，不伪造数字。**按需测量，不自动轮询**——
 *   实测这个 API 单次调用要遍历整个进程的堆，耗时以十秒计（本机实测
 *   ~20s），跟其余每 500ms 刷新一次的读数完全不是一个量级；自动轮询会
 *   堆积一串互相竞态的慢请求，一个"性能面板"自己先把浏览器拖慢，属实
 *   本末倒置。
 *

 * 包体预算（`scripts/check-bundle-budget.mjs`）与历史内存预算（`History`
 * 自身强制执行）不是"这一帧的读数"，这里只把目标/硬上限列出来供对照，
 * 不重复测量。
 */
import { Show, createSignal, onCleanup, onMount } from 'solid-js';
import { PERF_BUDGET } from '../../app/perf/metrics';
import styles from './PerfPanel.module.css';

export interface PerfPanelBridge {
  readonly compositedTiles: () => number;
  readonly lastGpuSubmitMs: () => number | null;
}

export interface PerfPanelProps {
  readonly bridge: PerfPanelBridge;
}

interface MemoryBreakdown {
  readonly bytes: number;
}

/** `performance.measureUserAgentSpecificMemory()` 不在 TS 标准 lib 里
 * （提案阶段的 API），且只在跨源隔离下才存在——运行时特性探测，不是类型断言。 */
function measureMemory(): Promise<MemoryBreakdown> | null {
  const perf = performance as unknown as Record<string, unknown>;
  const fn = perf['measureUserAgentSpecificMemory'];
  return typeof fn === 'function'
    ? (fn.call(performance) as Promise<MemoryBreakdown>)
    : null;
}

const POLL_INTERVAL_MS = 500;

export function PerfPanel(props: PerfPanelProps): ReturnType<typeof Show> {
  const [open, setOpen] = createSignal(false);
  const [tilesPerSecond, setTilesPerSecond] = createSignal(0);
  const [gpuSubmitMs, setGpuSubmitMs] = createSignal<number | null>(null);
  const [memoryMb, setMemoryMb] = createSignal<number | null>(null);
  const [memoryUnavailable, setMemoryUnavailable] = createSignal(false);

  const [memoryPending, setMemoryPending] = createSignal(false);
  let lastTiles = props.bridge.compositedTiles();

  onMount(() => {
    const timer = setInterval(() => {
      const total = props.bridge.compositedTiles();
      setTilesPerSecond(Math.round(((total - lastTiles) * 1000) / POLL_INTERVAL_MS));
      lastTiles = total;
      setGpuSubmitMs(props.bridge.lastGpuSubmitMs());
    }, POLL_INTERVAL_MS);
    onCleanup(() => clearInterval(timer));
  });

  const requestMemoryMeasurement = (): void => {
    if (memoryPending()) return; // 单次要跑十几秒，重复点击不叠加请求
    const request = measureMemory();
    if (request === null) {
      setMemoryUnavailable(true);
      return;
    }
    setMemoryPending(true);
    void request
      .then((result) => setMemoryMb(result.bytes / (1024 * 1024)))
      .catch(() => setMemoryUnavailable(true))
      .finally(() => setMemoryPending(false));
  };

  const overBudget = (value: number | null, budget: { readonly hard: number }): boolean =>
    value !== null && value > budget.hard;

  return (
    <div class={styles.root}>
      <button
        type="button"
        class={styles.toggle}
        data-testid="perf-panel-toggle"
        onClick={() => setOpen((v) => !v)}
      >
        性能
      </button>
      <Show when={open()}>
        <div class={styles.panel} data-testid="perf-panel">
          <div class={styles.row} classList={{ [styles.overBudget!]: gpuSubmitMs() !== null && overBudget(gpuSubmitMs(), PERF_BUDGET.viewportFps) }}>
            <span class={styles.label}>本轮重算块数/秒</span>
            <span data-testid="perf-panel-tiles">{tilesPerSecond()}</span>
          </div>
          <div class={styles.row}>
            <span class={styles.label}>GPU 帧提交耗时</span>
            <span data-testid="perf-panel-gpu-submit">
              {gpuSubmitMs() === null ? '—' : `${gpuSubmitMs()!.toFixed(1)} ms`}
            </span>
          </div>
          <div class={styles.row}>
            <span class={styles.label}>常驻内存</span>
            <span data-testid="perf-panel-memory">
              {memoryUnavailable()
                ? '不可用（未跨源隔离）'
                : memoryPending()
                  ? '测量中…（单次约需十几秒）'
                  : memoryMb() === null
                    ? '尚未测量'
                    : `${memoryMb()!.toFixed(0)} MB（预算 ${PERF_BUDGET.residentMemoryMb.target}/${PERF_BUDGET.residentMemoryMb.hard} MB）`}
            </span>
            <Show when={!memoryUnavailable()}>
              <button
                type="button"
                class={styles.toggle}
                data-testid="perf-panel-measure-memory"
                disabled={memoryPending()}
                onClick={requestMemoryMeasurement}
              >
                测量
              </button>
            </Show>
          </div>
          <div class={styles.row}>
            <span class={styles.label}>历史内存预算</span>
            <span>
              {(PERF_BUDGET.historyBudgetBytes.target / (1024 * 1024)).toFixed(0)} MB（DESIGN.md §4.5，由 History 强制执行）
            </span>
          </div>
          <div class={styles.row}>
            <span class={styles.label}>核心包体（gzip）</span>
            <span>
              目标 {(PERF_BUDGET.bundleGzipKb.target / 1024).toFixed(1)} MB / 硬上限{' '}
              {(PERF_BUDGET.bundleGzipKb.hard / 1024).toFixed(1)} MB（见 pnpm build 后 dist/stats.json）
            </span>
          </div>
        </div>
      </Show>
    </div>
  );
}
